/**
 * GHL embeds Studio in a cross-origin iframe. When the iframe document is
 * scrolled to its top/bottom and the user keeps scrolling, browsers often
 * chain the wheel to the parent CRM page. That slides the iframe up and
 * clips the sticky Studio navbar (and the CRM header logo) in half.
 *
 * `overscroll-behavior` on html is not reliable across browsers for
 * cross-origin iframes, so we also preventDefault on wheel events that no
 * scrollable ancestor can absorb.
 */

const SCROLLABLE_OVERFLOW = new Set(['auto', 'scroll', 'overlay']);

export function isVerticallyScrollable(el: Element, style: CSSStyleDeclaration): boolean {
  if (el === document.documentElement || el === document.body) {
    return el.scrollHeight > el.clientHeight + 1;
  }
  return (
    SCROLLABLE_OVERFLOW.has(style.overflowY) &&
    el.scrollHeight > el.clientHeight + 1
  );
}

/** True when `el` can still move in the direction of `deltaY`. */
export function canElementScrollFurther(
  el: Element,
  deltaY: number,
  epsilon = 1,
): boolean {
  if (deltaY === 0) return false;
  const maxScroll = el.scrollHeight - el.clientHeight;
  if (maxScroll <= epsilon) return false;
  if (deltaY < 0) return el.scrollTop > epsilon;
  return el.scrollTop < maxScroll - epsilon;
}

/**
 * Walk from the event target up to the document. If any ancestor can still
 * scroll in this wheel direction, the event should proceed. Otherwise the
 * wheel would chain to the parent frame and should be blocked.
 */
export function shouldPreventIframeScrollChain(
  target: EventTarget | null,
  deltaY: number,
  getStyle: (el: Element) => CSSStyleDeclaration = (el) =>
    window.getComputedStyle(el),
): boolean {
  if (deltaY === 0) return false;

  let el: Element | null =
    target instanceof Element
      ? target
      : target instanceof Text
        ? target.parentElement
        : null;

  while (el) {
    const style = getStyle(el);
    if (
      isVerticallyScrollable(el, style) &&
      canElementScrollFurther(el, deltaY)
    ) {
      return false;
    }
    if (el === document.documentElement) break;
    el = el.parentElement;
  }

  // Also check the root scrolling element when the walk started inside a
  // non-scrollable tree (or target was null).
  const root = document.scrollingElement ?? document.documentElement;
  if (canElementScrollFurther(root, deltaY)) {
    return false;
  }

  return true;
}

export function attachIframeScrollChainGuard(
  win: Window = window,
): () => void {
  const onWheel = (event: WheelEvent) => {
    if (
      shouldPreventIframeScrollChain(event.target, event.deltaY)
    ) {
      event.preventDefault();
    }
  };

  win.addEventListener('wheel', onWheel, { passive: false });
  return () => {
    win.removeEventListener('wheel', onWheel);
  };
}
